import { Loader } from "common";
import React, { useEffect, useState } from "react";
import { AppButton } from "site/ui";
import { useQuizStore } from "store";
import styled from "styled-components";
import { IQuestionAnswer, IQuiz } from "utils/types";

const ReportCardContent = styled.div`
  background-color: white;
  border-radius: 16px;
  padding: 20px;
  overflow-y: auto;
  position: relative;
  // min-width: 400px;
`;

const ReportCardTitle = styled.h2`
  font-size: 18px;
  font-weight: 500;
  color: #111827;
  margin-top: 0;
  margin-bottom: 24px;
`;

const ScoreSummary = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const SummaryText = styled.h3`
  font-size: 16px;
  font-weight: 500;
  color: #111827;
  margin: 0;
`;

const ScoreDisplay = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const QuestionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const QuestionItem = styled.div`
  margin-bottom: 12px;
`;

const QuestionNumber = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  margin-bottom: 8px;
`;

const AnswerRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 14px;
  color: #6b7280;
`;

const CorrectIcon = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #22c55e;
  flex-shrink: 0;
  margin-top: 2px;
`;

const IncorrectIcon = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #ef4444;
  flex-shrink: 0;
  margin-top: 2px;
`;

const AnswerText = styled.span`
  flex-grow: 1;
`;

export interface ReportCardProps {
  onTakeQuizAgain: () => void;
  onGetCorrectAnswers: (quizId: string) => Promise<IQuestionAnswer[]>;
  onGetQuiz: (quizId: string) => Promise<IQuiz|undefined>;
}

export const ReportCard: React.FC<ReportCardProps> = ({
  onTakeQuizAgain, onGetCorrectAnswers, onGetQuiz
}) => {
  const [loading, setLoading] = useState(false);
  const [quizCorrectAnswers, setCorrectAnswers] = useState<IQuestionAnswer[]>([])
  const [quiz, setQuiz] = useState<IQuiz>();
  const { quiz: quizYear, userQuiz: quizReport, quizAnswers} = useQuizStore();

  useEffect(() => {
    if(quizYear?.quizId){
      (async () => {
        setLoading(true)
        const [quiz, quizAnswers] = await Promise.all([
          onGetQuiz(quizYear.quizId),
          onGetCorrectAnswers(quizYear.quizId), 
        ]);
        setCorrectAnswers(quizAnswers)
        setQuiz(quiz)
        setLoading(false);
      })()
    }
  }, [JSON.stringify(quizYear)])

  if(loading){
    return <Loader/>
  }

  return (<ReportCardContent>
        <ReportCardTitle>Report card</ReportCardTitle>

        <ScoreSummary>
          <SummaryText>Summary</SummaryText>
          <ScoreDisplay>
            {quizReport?.score || 0} / {quizReport?.totalScore || 0}
          </ScoreDisplay>
        </ScoreSummary>

        <QuestionList>
          {quiz?.questions?.map((question, index) => {
            const correctOptionIds = quizCorrectAnswers.find(item => item.questionId === question.id)?.optionIds
            const yourOptionIds = quizAnswers?.find(item => item.questionId === question.id)?.optionIds
            let correctAns = "";
            let yourAns = ""
            if(correctOptionIds){
              correctAns = question.options?.find(item => correctOptionIds.includes(item.id))?.text || ""
            }
            if(yourOptionIds){
              yourAns = question.options?.find(item => yourOptionIds.includes(item.id))?.text || ""
            }
            const isCorrect = correctOptionIds === yourOptionIds
            return (
              <QuestionItem key={question.id}>
                <QuestionNumber>
                  {index + 1}. {question.content}
                </QuestionNumber>
                <AnswerRow>
                  <CorrectIcon />
                  <AnswerText>Correct answer: {correctAns}</AnswerText>
                </AnswerRow>
                <AnswerRow>
                  {isCorrect ? <CorrectIcon /> : <IncorrectIcon />}
                  <AnswerText>Your answer: {yourAns}</AnswerText>
                </AnswerRow>
              </QuestionItem>
            )
        })}
        </QuestionList>

        <div style={{ marginTop: "24px", width: "100%" }}>
          <AppButton variant="primary" fullWidth onClick={onTakeQuizAgain}>
            Take quiz again
          </AppButton>
        </div>
      </ReportCardContent>
  );
};

export default ReportCard;
